/**
 * Coding Agent Manifest
 *
 * Operation manifest for "Coding Agent" — an Agent action whose reasoning loop
 * runs inside an Agent Harness sandbox (GitHub Copilot today). Cloned repos,
 * skills, and input files are wired in via the Agent Harness tab, and the
 * sandbox produces a structured set of `fileOutputs` alongside the normal
 * assistant message output.
 *
 * Shares the same connection (`agent`) and connector as agentloop.ts; the
 * action is still emitted with `type: 'Agent'`. The variant is detected from
 * `inputs.parameters.agentModelSettings.agentHarness.type` (see
 * `isCodingAgentAction`) which routes the node to this manifest instead of
 * the base agent manifest.
 */

import type { OperationManifest, ConnectorInfo } from '@/types/operationManifest';
import { OperationOptions, SettingScope } from '@/types/operationManifest';
import agentLoopManifest from './agentloop';

// Coding-agent badge: green rounded square with the same bot-head silhouette
// rendered by `CodingAgentGlyph` (components/icons/CodingAgentGlyph.tsx).
// Kept as inline SVG so the source stays human-readable; encoded once at
// module load. The picker list / search results show this rasterized data
// URI, while the canvas node and "Choose an Agent" card render the React
// glyph — same shape, different color hosts.
const CODING_AGENT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">' +
    '<rect width="22" height="22" rx="4" fill="#0E7C61"/>' +
    '<circle cx="11" cy="3.6" r="1" fill="#EEEFFF"/>' +
    '<line x1="11" y1="4.6" x2="11" y2="6" stroke="#EEEFFF" stroke-width="1.2" stroke-linecap="round"/>' +
    '<rect x="4.5" y="6" width="13" height="11" rx="3" fill="#EEEFFF" fill-opacity="0.18" stroke="#EEEFFF" stroke-width="1"/>' +
    '<rect x="3" y="9.5" width="1.6" height="4" rx="0.6" fill="#EEEFFF"/>' +
    '<rect x="17.4" y="9.5" width="1.6" height="4" rx="0.6" fill="#EEEFFF"/>' +
    '<circle cx="8.7" cy="10" r="1.1" fill="#EEEFFF"/>' +
    '<circle cx="13.3" cy="10" r="1.1" fill="#EEEFFF"/>' +
    '<path d="M8.4 13.6 L7 14.6 L8.4 15.6" stroke="#EEEFFF" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
    '<path d="M13.6 13.6 L15 14.6 L13.6 15.6" stroke="#EEEFFF" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
    '<path d="M12 13.4 L10 15.8" stroke="#EEEFFF" stroke-width="1.1" stroke-linecap="round" fill="none"/>' +
  '</svg>';
const CODING_AGENT_ICON_URI = `data:image/svg+xml;utf8,${encodeURIComponent(CODING_AGENT_SVG)}`;

const baseInputProperties = agentLoopManifest.properties.inputs?.properties as Record<string, unknown>;
const baseAgentModelSettings = baseInputProperties.agentModelSettings as {
  type: string;
  properties: Record<string, unknown>;
  'x-ms-visibility'?: string;
};

export default {
  properties: {
    iconUri: CODING_AGENT_ICON_URI,
    brandColor: '#0E7C61',
    summary: 'Coding Agent',
    description:
      'Agent loop that runs inside a sandboxed harness (e.g. GitHub Copilot) with cloned repositories, skills, and input files. Produces structured file outputs in addition to the assistant message.',

    allowChildOperations: true,
    subGraphDetails: agentLoopManifest.properties.subGraphDetails,
    inputs: {
      type: 'object',
      properties: {
        ...baseInputProperties,
        agentModelSettings: {
          ...baseAgentModelSettings,
          properties: {
            ...baseAgentModelSettings.properties,
            agentHarness: {
              type: 'object',
              title: 'Agent Harness',
              description: 'Sandboxed execution environment for the coding agent.',
              'x-ms-visibility': 'advanced',
              properties: {
                type: {
                  type: 'string',
                  title: 'Harness type',
                  description: 'Execution engine for the sandbox.',
                  default: 'GHCP',
                  'x-ms-editor': 'dropdown',
                  'x-ms-editor-options': {
                    options: [
                      {
                        value: 'GHCP',
                        displayName: 'GHCP (GitHub Copilot)',
                      },
                    ],
                  },
                },
                sandboxConfigurationName: {
                  type: 'string',
                  title: 'Sandbox configuration',
                  description: 'Name of the sandbox configuration to apply.',
                },
                skills: {
                  type: 'array',
                  title: 'Skills',
                  description: 'Repositories to clone into the sandbox as skills.',
                  items: {
                    type: 'object',
                    properties: {
                      repository: { type: 'string', title: 'Repository' },
                      folders: {
                        type: 'array',
                        title: 'Folders',
                        items: { type: 'string' },
                      },
                    },
                  },
                },
                inputFiles: {
                  type: 'array',
                  title: 'Input Files',
                  description: 'Files to upload to the sandbox workspace before the agent runs. Use expressions to pass content from previous actions.',
                  'x-ms-visibility': 'important',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelSettings.agentHarness.type',
                        excludeValues: [null, ''],
                      },
                    ],
                  },
                  items: {
                    type: 'object',
                    properties: {
                      name: {
                        type: 'string',
                        title: 'File Name',
                        'x-ms-summary': 'File Name',
                        description: 'Name of the file in the sandbox workspace (e.g., data.csv, report.pdf)',
                      },
                      content: {
                        type: 'string',
                        title: 'Content',
                        'x-ms-summary': 'Content',
                        description: 'File content — use expressions like @{body(\'Get_Blob\')} for dynamic content',
                      }
                    },
                    required: ['name', 'content'],
                  },
                },
              },
            },
          },
        },
      },
      required: agentLoopManifest.properties.inputs?.required,
    },
    outputs: {
      type: 'object',
      properties: {
        lastAssistantMessage: {
          type: 'object',
          title: 'Last Assistant Message',
          description: 'This is the final message returned by the model',
        },
        fileOutputs: {
          type: 'array',
          title: 'File Outputs',
          description: 'Files produced during agent harness execution',
          items: {
            type: 'object',
            properties: {
              path: {
                type: 'string',
                title: 'Path',
                description: 'File path relative to the agent session workspace',
              },
              content: {
                title: 'Content',
                description:
                  'File content — a plain string for text files, or a content envelope object ({"$content-type":"...","$content":"base64..."}) for binary files.',
              },
              size: {
                type: 'integer',
                title: 'Size',
                description: 'File size in bytes',
              },
            },
          },
        },
      },
    },
    isOutputsOptional: false,
    channels: {
      type: 'object',
      properties: {
        in: {
          type: 'object',
        },
        out: {},
      },
    },
    inputsLocation: ['inputs', 'parameters'],
    isInputsOptional: false,

    supportedChannels: [
      {
        input: {
          type: 'Request',
        },
        output: {
          type: 'Response',
        },
      },
    ],

    connection: {
      required: true,
      type: 'agent',
    },

    connectionReference: {
      referenceKeyFormat: 'agentconnection',
    },

    connector: {
      id: '/connectionProviders/agent',
      name: 'Agent',
      properties: {
        description: 'Agent operations',
        displayName: 'Agent',
      },
      isSecureByDefault: true,
    } as ConnectorInfo,

    settings: {
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
      timeout: {
        scopes: [SettingScope.Action],
      },
      count: {
        scopes: [SettingScope.Action],
      },
      retryPolicy: {
        scopes: [SettingScope.Action],
      },
      operationOptions: {
        scopes: [SettingScope.Action],
        options: [OperationOptions.FailWhenLimitsReached],
      },
    },
  },
} as OperationManifest;
